import {
  canCancelSession,
  canRescheduleSession,
  isAdminLegacyMomentsSession,
} from '../coachSchedule/sessionData';

import type { AdminAppointment } from '../appointments/types';
import type { Session } from '@/types/session';

export type AdminMemberSessionRow =
  | {
      key: string;
      source: 'native';
      appointment: AdminAppointment;
      railsSession?: Session;
    }
  | { key: string; source: 'legacy'; session: Session };

export interface AdminMemberSessionDiagnostic {
  reason:
    | 'conflicting_markers'
    | 'multiple_claims'
    | 'identity_mismatch'
    | 'invalid_identity';
  sessionId: string;
  claimedIds: string[];
}

const validId = (id: unknown): id is string =>
  typeof id === 'string' && id.trim().length > 0;

export const adminMemberRowStartAt = (row: AdminMemberSessionRow): string =>
  row.source === 'native'
    ? row.appointment.currentSchedule.startAt
    : row.session.startedAt;
export const adminMemberRowEndAt = (row: AdminMemberSessionRow): string =>
  row.source === 'native'
    ? row.appointment.currentSchedule.endAt
    : row.session.endedAt;
export const adminMemberRowIsCancelled = (row: AdminMemberSessionRow) =>
  row.source === 'native'
    ? row.appointment.lifecycleStatus === 'cancelled'
    : row.session.status === 'cancelled';
export const adminMemberRowIsUpcoming = (
  row: AdminMemberSessionRow,
  now: number,
): boolean =>
  (row.source === 'native'
    ? row.appointment.bookingStatus === 'confirmed' &&
      row.appointment.lifecycleStatus === 'scheduled'
    : row.session.status === 'active' ||
      row.session.status === 'rescheduled') &&
  Date.parse(adminMemberRowEndAt(row)) >= now;
// MEN-5338: Ops cancel/reschedule affordance. The history read zeroes
// action_capabilities (MEN-5211), so this gate is deliberately conservative and
// the authoritative selected-pair appointment decides on click. Each row is
// classified by its own source, never by a coach flag: native rows and
// Rails-owned Moments series are actionable, and Cal.com rows stay read-only.
// Rails refuses (403) an Ops action when Moments does not own the coach's
// calendar.
export interface AdminMemberRowActions {
  cancel: boolean;
  reschedule: boolean;
}

const NO_ACTIONS: AdminMemberRowActions = { cancel: false, reschedule: false };

// Cancel and Reschedule are gated independently (PR #1710 review): Rails can
// allow one without the other, e.g. a Moments chemistry call inside 48 hours.
export const adminMemberRowActions = (
  row: AdminMemberSessionRow,
  now: number,
): AdminMemberRowActions => {
  if (
    !adminMemberRowIsUpcoming(row, now) ||
    Date.parse(adminMemberRowStartAt(row)) <= now
  )
    return NO_ACTIONS;
  if (row.source === 'native') {
    // Capabilities are unknown until the click-time selected-pair read, which
    // refuses a denied action, so both are offered here.
    const ready =
      validId(row.appointment.expectedIdentities.coach.railsUserId) &&
      validId(row.appointment.expectedIdentities.member.railsUserId);
    return { cancel: ready, reschedule: ready };
  }
  // Rails-owned Moments series go through the Ops Session routes; Rails'
  // own availability flags gate which Sessions it will change.
  const { session } = row;
  if (
    !isAdminLegacyMomentsSession(session) ||
    !validId(session.coachId) ||
    !validId(session.userId)
  )
    return NO_ACTIONS;
  return {
    cancel: canCancelSession(session, true),
    reschedule: canRescheduleSession(session, true),
  };
};

export const adminMemberRowIsActionable = (
  row: AdminMemberSessionRow,
  now: number,
): boolean => {
  const actions = adminMemberRowActions(row, now);
  return actions.cancel || actions.reschedule;
};

export const findAdminMemberSessionAnchor = (
  rows: AdminMemberSessionRow[],
  now: number,
): string | null => {
  const upcoming = rows.find((row) => adminMemberRowIsUpcoming(row, now));
  if (upcoming) return upcoming.key;
  // Rows are already in current-start chronology; future tombstones are not history.
  const past = rows.filter((row) => Date.parse(adminMemberRowEndAt(row)) < now);
  return past[past.length - 1]?.key ?? rows[0]?.key ?? null;
};

/* eslint-disable @typescript-eslint/no-unnecessary-condition -- Fail closed for malformed runtime identities even though decoded types require them. */
export const composeAdminMemberSessionRows = ({
  memberId,
  appointments,
  sessions,
}: {
  memberId: string;
  appointments: AdminAppointment[];
  sessions: Session[];
}): {
  rows: AdminMemberSessionRow[];
  diagnostics: AdminMemberSessionDiagnostic[];
} => {
  const uniqueSessions = new Map<string, Session>();
  sessions.forEach((session) => {
    if (!uniqueSessions.has(session.id))
      uniqueSessions.set(session.id, session);
  });
  const consumed = new Set<string>();
  const latest = new Map<string, AdminAppointment>();
  appointments.forEach((appointment) => {
    const current = latest.get(appointment.id);
    if (!current || appointment.version > current.version)
      latest.set(appointment.id, appointment);
  });
  const diagnostics: AdminMemberSessionDiagnostic[] = [];
  const claims = new Map<string, Session[]>();
  const tainted = new Set<string>();
  uniqueSessions.forEach((session) => {
    const claimedIds = [
      ...new Set(
        [session.sourceAppointmentId, session.momentsBookingId].filter(
          (id): id is string => Boolean(id),
        ),
      ),
    ];
    if (
      !validId(memberId) ||
      !validId(session.id) ||
      !validId(session.userId) ||
      !validId(session.coachId) ||
      session.user?.id !== session.userId ||
      session.coach?.id !== session.coachId
    ) {
      diagnostics.push({
        claimedIds,
        reason: 'invalid_identity',
        sessionId: session.id,
      });
      uniqueSessions.delete(session.id);
      return;
    }
    if (session.userId !== memberId) {
      uniqueSessions.delete(session.id);
      return;
    }
    if (claimedIds.length > 1) {
      claimedIds.forEach((id) => tainted.add(id));
      diagnostics.push({
        claimedIds,
        reason: 'conflicting_markers',
        sessionId: session.id,
      });
    } else if (claimedIds.length === 1) {
      const id = claimedIds[0];
      if (id) claims.set(id, [...(claims.get(id) ?? []), session]);
    }
  });
  claims.forEach((claimants, id) => {
    if (claimants.length > 1)
      claimants.forEach((session) =>
        diagnostics.push({
          claimedIds: [id],
          reason: 'multiple_claims',
          sessionId: session.id,
        }),
      );
  });
  const rows: AdminMemberSessionRow[] = [];
  latest.forEach((appointment) => {
    const identities = appointment.expectedIdentities;
    if (
      !validId(memberId) ||
      !validId(appointment.id) ||
      !validId(identities?.coach?.railsUserId) ||
      !validId(identities?.member?.railsUserId) ||
      identities.member.railsUserId !== memberId
    ) {
      // Native-only diagnostics use the appointment id as sessionId.
      diagnostics.push({
        claimedIds: [appointment.id],
        reason: 'invalid_identity',
        sessionId: appointment.id,
      });
      return;
    }
    const claimants = claims.get(appointment.id) ?? [];
    let railsSession =
      claimants.length === 1 && !tainted.has(appointment.id)
        ? claimants[0]
        : undefined;
    if (
      railsSession &&
      (railsSession.userId !== identities.member.railsUserId ||
        railsSession.coachId !== identities.coach.railsUserId)
    ) {
      diagnostics.push({
        claimedIds: [appointment.id],
        reason: 'identity_mismatch',
        sessionId: railsSession.id,
      });
      railsSession = undefined;
    }
    if (railsSession) consumed.add(railsSession.id);
    rows.push({
      appointment,
      key: `appointment:${appointment.id}`,
      source: 'native',
      ...(railsSession ? { railsSession } : {}),
    });
  });
  uniqueSessions.forEach((session) => {
    if (!consumed.has(session.id))
      rows.push({ key: `session:${session.id}`, session, source: 'legacy' });
  });
  rows.sort(
    (a, b) =>
      Date.parse(adminMemberRowStartAt(a)) -
        Date.parse(adminMemberRowStartAt(b)) || a.key.localeCompare(b.key),
  );
  return { diagnostics, rows };
};
/* eslint-enable @typescript-eslint/no-unnecessary-condition */
